/**
 * The vendor console's contract (PLAN §13 phase 7b items 1 and 5, D13, D15,
 * D27): tenants with their counts and their suspension, the cross-tenant ops
 * view (nodes, live productions, alarms), releases and the internal pins,
 * the vendor's own activity log, and announcements — vendor-authored notices
 * and incidents shown in-app to the tenants they target and, when public, on
 * the status page (`statusPage.ts`).
 *
 * The routes are `vendorRoutes.ts`. Every one under `/vendor` is the vendor
 * tenant's only (`vendor.ops` to read, `vendor.admin` to write). Two are not:
 * `GET /announcements` (any signed-in user: what is shown to THEIR tenant,
 * without the targeting) and `GET /public/announcements` (no credential: the
 * status page's public incidents).
 *
 * **A tenant's status.** `suspended` refuses the tenant's users (every route
 * answers `suspended`, 403, naming the reason; the WebSocket refuses to
 * connect) and its nodes' site links (refused with the reason; a node keeps
 * running its productions locally — docs/runtime.md). The vendor tenant can
 * never be suspended.
 */
import { ulidSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';
import { releasePinWireSchema, sha256HexWireSchema } from './releases.js';
import { siteLinkStateWireSchema, siteNodeRoleOpenWireSchema } from './sites.js';
import { siteIdWireSchema, studioIdWireSchema } from './studios.js';
import { emailWireSchema, tenantIdWireSchema, userIdWireSchema } from './tenancy.js';
import { witnessInForceWireSchema } from './witness.js';

const isoSchema = z.iso.datetime({ offset: true });

// ─── tenants ───

export const TENANT_STATUSES = ['active', 'suspended'] as const;
export const tenantStatusWireSchema = z.enum(TENANT_STATUSES);
export type TenantStatusWire = z.infer<typeof tenantStatusWireSchema>;

/** A tenant's usage and footprint, counted when read. */
export const tenantCountsWireSchema = z
  .object({
    users: z.number().int().nonnegative(),
    sites: z.number().int().nonnegative(),
    /** Active (not revoked) nodes across its sites. */
    nodes: z.number().int().nonnegative(),
    /** Productions whose status is `active` (on-prem and cloud alike). */
    liveProductions: z.number().int().nonnegative(),
    /** Cloud-production hours metered this calendar month (UTC; PLAN §9.6). */
    cloudProductionHoursThisMonth: z.number().nonnegative(),
  })
  .strict();
export type TenantCountsWire = z.infer<typeof tenantCountsWireSchema>;

export const vendorTenantWireSchema = z
  .object({
    tenantId: tenantIdWireSchema,
    name: z.string(),
    createdAt: z.string(),
    status: tenantStatusWireSchema,
    suspendedAt: isoSchema.nullable(),
    suspendedReason: z.string().nullable(),
    isVendor: z.boolean(),
    counts: tenantCountsWireSchema,
  })
  .strict();
export type VendorTenantWire = z.infer<typeof vendorTenantWireSchema>;

export const tenantPathWireSchema = z.object({ tenantId: tenantIdWireSchema.describe('The tenant’s id.') }).strict();

export const suspendTenantRequestWireSchema = z
  .object({
    /** Shown to the tenant's users and nodes with every refusal. */
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type SuspendTenantRequestWire = z.infer<typeof suspendTenantRequestWireSchema>;

export const reactivateTenantRequestWireSchema = z
  .object({
    /** Why, for the vendor's activity log. */
    note: z.string().max(500),
  })
  .strict();
export type ReactivateTenantRequestWire = z.infer<typeof reactivateTenantRequestWireSchema>;

// ─── alarms (D27; the conditions of `@acp/tenant-alerts/conditions.js`) ───

/** The conditions the ops view evaluates on the latest status (PLAN §11.1's alarms that are states, not events). */
export const ALARM_CONDITIONS = ['server-offline-live', 'standby-missing', 'cert-expiring', 'clock-drift', 'device-disconnected-on-air', 'standby-out-of-sync'] as const;
export const alarmConditionWireSchema = z.enum(ALARM_CONDITIONS);
export type AlarmConditionWire = z.infer<typeof alarmConditionWireSchema>;

export const alarmSeverityWireSchema = z.enum(['info', 'warning', 'critical']);
export type AlarmSeverityWire = z.infer<typeof alarmSeverityWireSchema>;

export const alarmWireSchema = z
  .object({
    type: alarmConditionWireSchema,
    /** Stable across evaluations while the condition holds (dedup key). */
    key: z.string().min(1).max(300),
    severity: alarmSeverityWireSchema,
    tenantId: tenantIdWireSchema,
    siteId: z.string(),
    studioId: z.string().nullable(),
    /** What it is about: a node's label, a production id, a device id. */
    subject: z.string(),
    summary: z.string(),
    detail: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
  })
  .strict();
export type AlarmWire = z.infer<typeof alarmWireSchema>;

// ─── the ops view ───

export const vendorNodeWireSchema = z
  .object({
    tenantId: tenantIdWireSchema,
    siteId: siteIdWireSchema,
    siteName: z.string(),
    nodeId: z.string(),
    label: z.string(),
    role: siteNodeRoleOpenWireSchema,
    linkState: siteLinkStateWireSchema,
    lastSeen: isoSchema.nullable(),
    witness: witnessInForceWireSchema.nullable(),
    packageHashes: z.array(z.string()),
    certExpiresAt: isoSchema.nullable(),
    clockDriftMs: z.number().nullable(),
    healthReportedAt: isoSchema.nullable(),
    /** Productions the node's last health report named, with its role for each. */
    productions: z.array(z.object({ productionId: z.string(), role: z.enum(['active', 'standby']) }).strict()),
  })
  .strict();
export type VendorNodeWire = z.infer<typeof vendorNodeWireSchema>;

/** `single`: no pair (a production outside main/backup); otherwise the playout state's `redundancy.protection`. */
export const vendorProtectionWireSchema = z.enum(['protected', 'syncing', 'unprotected', 'single', 'unknown']);

export const vendorProductionWireSchema = z
  .object({
    tenantId: tenantIdWireSchema,
    productionId: ulidSchema,
    studioId: studioIdWireSchema,
    siteId: z.string().nullable(),
    activeNode: z.string().nullable(),
    /** Something is on air (the playout state has a current part). */
    onAir: z.boolean(),
    protection: vendorProtectionWireSchema,
    /** The cloud's last playout snapshot is older than the site link's stale window, or there is none. */
    stale: z.boolean(),
    devices: z.object({ total: z.number().int().nonnegative(), disconnected: z.number().int().nonnegative() }).strict(),
    packageHash: z.string().nullable(),
  })
  .strict();
export type VendorProductionWire = z.infer<typeof vendorProductionWireSchema>;

export const vendorOpsResponseWireSchema = z
  .object({
    generatedAt: isoSchema,
    tenants: z.array(z.object({ tenantId: tenantIdWireSchema, name: z.string(), status: tenantStatusWireSchema }).strict()),
    nodes: z.array(vendorNodeWireSchema),
    productions: z.array(vendorProductionWireSchema),
    alarms: z.array(alarmWireSchema),
  })
  .strict();
export type VendorOpsResponseWire = z.infer<typeof vendorOpsResponseWireSchema>;

export const vendorTenantResponseWireSchema = z.object({ tenant: vendorTenantWireSchema }).strict();
export const listVendorTenantsResponseWireSchema = z.object({ tenants: z.array(vendorTenantWireSchema) }).strict();
export const vendorTenantDetailResponseWireSchema = z
  .object({
    tenant: vendorTenantWireSchema,
    sites: z.array(z.object({ siteId: siteIdWireSchema, name: z.string(), kind: z.enum(['onprem', 'cloud']) }).strict()),
    nodes: z.array(vendorNodeWireSchema),
    productions: z.array(vendorProductionWireSchema),
    alarms: z.array(alarmWireSchema),
  })
  .strict();
export type VendorTenantDetailWire = z.infer<typeof vendorTenantDetailResponseWireSchema>;

// ─── releases and pins (D15) ───

export const vendorReleaseWireSchema = z
  .object({
    listHash: sha256HexWireSchema,
    commit: z.string(),
    /** When its list was published (the store's timestamp), or null if unknown. */
    publishedAt: isoSchema.nullable(),
    packages: z.array(z.object({ name: z.string(), hash: sha256HexWireSchema }).strict()),
  })
  .strict();
export type VendorReleaseWire = z.infer<typeof vendorReleaseWireSchema>;

export const rolloutSourceWireSchema = z.enum(['site-pin', 'stage-pin', 'latest']);

export const rolloutNodeWireSchema = z
  .object({
    tenantId: tenantIdWireSchema,
    siteId: siteIdWireSchema,
    nodeId: z.string(),
    label: z.string(),
    role: siteNodeRoleOpenWireSchema,
    linkState: siteLinkStateWireSchema,
    /** What the manifest serves this node (site pin → stage pin → latest), and that list; null when nothing is published. */
    target: z.object({ source: rolloutSourceWireSchema, listHash: sha256HexWireSchema.nullable() }).strict(),
    /** The newest known release every package of which the node reports holding; null when none. */
    running: sha256HexWireSchema.nullable(),
    upToDate: z.boolean(),
  })
  .strict();
export type RolloutNodeWire = z.infer<typeof rolloutNodeWireSchema>;

export const sitePinViewWireSchema = z
  .object({
    /** The tenant whose site is pinned; null for a legacy pin, keyed by site id alone. */
    tenantId: tenantIdWireSchema.nullable(),
    siteId: siteIdWireSchema,
    /**
     * A pin written before pins were tenant-qualified (`pins/sites/<siteId>.json`): it still holds every
     * tenant's site of that id that has no pin of its own (never the cloud site), until re-pinned or cleared.
     */
    legacy: z.boolean(),
    pin: releasePinWireSchema,
  })
  .strict();
export type SitePinViewWire = z.infer<typeof sitePinViewWireSchema>;

export const vendorReleasesResponseWireSchema = z
  .object({
    /** The latest release (`packages.json`), or null before the first publish. */
    latest: sha256HexWireSchema.nullable(),
    /** The most recently published releases, newest first. */
    releases: z.array(vendorReleaseWireSchema),
    stagePin: releasePinWireSchema.nullable(),
    sitePins: z.array(sitePinViewWireSchema),
    rollout: z.array(rolloutNodeWireSchema),
  })
  .strict();
export type VendorReleasesResponseWire = z.infer<typeof vendorReleasesResponseWireSchema>;

export const setPinRequestWireSchema = z.object({ listHash: sha256HexWireSchema, note: z.string().max(512) }).strict();
export type SetPinRequestWire = z.infer<typeof setPinRequestWireSchema>;
export const pinResponseWireSchema = z.object({ pin: releasePinWireSchema.nullable() }).strict();
export const sitePinPathWireSchema = z.object({ tenantId: tenantIdWireSchema.describe('The tenant whose site it is.'), siteId: siteIdWireSchema.describe('The site’s id (unique within its tenant).') }).strict();

// ─── the vendor's activity log ───

export const VENDOR_ACTIONS = [
  'tenant.create',
  'tenant.suspend',
  'tenant.reactivate',
  'pin.set',
  'pin.clear',
  'announcement.create',
  'announcement.update',
  'announcement.delete',
] as const;
export const vendorActionWireSchema = z.enum(VENDOR_ACTIONS);
export type VendorActionWire = z.infer<typeof vendorActionWireSchema>;

export const vendorActivityWireSchema = z
  .object({
    activityId: z.string(),
    at: isoSchema,
    actor: z.object({ userId: userIdWireSchema, email: emailWireSchema }).strict(),
    action: vendorActionWireSchema,
    /** What it acted on: a tenant id, `stage` or `site:<siteId>`, an announcement id. */
    target: z.string(),
    detail: z.string(),
  })
  .strict();
export type VendorActivityWire = z.infer<typeof vendorActivityWireSchema>;

export const listVendorActivityQueryWireSchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(200).optional().describe('At most this many entries (default 50).'),
    before: z.string().max(200).optional().describe('The `next` cursor of a previous page.'),
  })
  .strict();
export const listVendorActivityResponseWireSchema = z.object({ entries: z.array(vendorActivityWireSchema), next: z.string().nullable() }).strict();

// ─── announcements ───

/** The status page's components (`statusPage.ts`), which an incident may name. */
export const STATUS_COMPONENTS = ['api', 'websocket', 'sign-in', 'cloud-productions'] as const;
export const statusComponentWireSchema = z.enum(STATUS_COMPONENTS);
export type StatusComponentWire = z.infer<typeof statusComponentWireSchema>;

export const announcementKindWireSchema = z.enum(['announcement', 'maintenance', 'incident']);
export type AnnouncementKindWire = z.infer<typeof announcementKindWireSchema>;
export const announcementSeverityWireSchema = z.enum(['info', 'minor', 'major', 'critical']);
export type AnnouncementSeverityWire = z.infer<typeof announcementSeverityWireSchema>;

const titleSchema = z.string().trim().min(1).max(200);
const bodySchema = z.string().max(5000);

export const announcementWireSchema = z
  .object({
    announcementId: ulidSchema,
    kind: announcementKindWireSchema,
    severity: announcementSeverityWireSchema,
    title: z.string(),
    body: z.string(),
    startsAt: isoSchema,
    /** Null: until it is ended. */
    endsAt: isoSchema.nullable(),
    /** Shown on the public status page (only when it targets every tenant). */
    public: z.boolean(),
    /** Null: every tenant. */
    tenantIds: z.array(tenantIdWireSchema).nullable(),
    components: z.array(statusComponentWireSchema),
    version: z.number().int().positive(),
    createdAt: isoSchema,
    createdBy: z.string(),
    updatedAt: isoSchema,
    updatedBy: z.string(),
  })
  .strict();
export type AnnouncementWire = z.infer<typeof announcementWireSchema>;

const announcementFields = {
  kind: announcementKindWireSchema,
  severity: announcementSeverityWireSchema,
  title: titleSchema,
  body: bodySchema,
  startsAt: isoSchema,
  endsAt: isoSchema.nullable(),
  public: z.boolean(),
  tenantIds: z.array(tenantIdWireSchema).min(1).max(500).nullable(),
  components: z.array(statusComponentWireSchema).max(STATUS_COMPONENTS.length),
};

/** A public announcement targets every tenant, and an end comes after the start. */
function coherent(value: { startsAt?: string | undefined; endsAt?: string | null | undefined; public?: boolean | undefined; tenantIds?: readonly string[] | null | undefined }, ctx: z.RefinementCtx): void {
  if (value.public === true && value.tenantIds !== undefined && value.tenantIds !== null) {
    ctx.addIssue({ code: 'custom', path: ['public'], message: 'a public announcement targets every tenant (tenantIds: null)' });
  }
  if (value.startsAt !== undefined && value.endsAt !== undefined && value.endsAt !== null && Date.parse(value.endsAt) <= Date.parse(value.startsAt)) {
    ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'endsAt must be after startsAt' });
  }
}

export const createAnnouncementRequestWireSchema = z.object(announcementFields).strict().superRefine(coherent);
export type CreateAnnouncementRequestWire = z.infer<typeof createAnnouncementRequestWireSchema>;

export const updateAnnouncementRequestWireSchema = z
  .object({ ...z.object(announcementFields).partial().shape, version: z.number().int().positive().describe('The version this edit was made from (409 if it moved).') })
  .strict()
  .superRefine(coherent);
export type UpdateAnnouncementRequestWire = z.infer<typeof updateAnnouncementRequestWireSchema>;

export const announcementPathWireSchema = z.object({ announcementId: ulidSchema.describe('The announcement’s id.') }).strict();
export const announcementResponseWireSchema = z.object({ announcement: announcementWireSchema }).strict();
export const listVendorAnnouncementsResponseWireSchema = z.object({ announcements: z.array(announcementWireSchema) }).strict();

/** What a tenant's users see: no targeting, no authorship. */
export const tenantAnnouncementWireSchema = z
  .object({
    announcementId: ulidSchema,
    kind: announcementKindWireSchema,
    severity: announcementSeverityWireSchema,
    title: z.string(),
    body: z.string(),
    startsAt: isoSchema,
    endsAt: isoSchema.nullable(),
    version: z.number().int().positive(),
  })
  .strict();
export type TenantAnnouncementWire = z.infer<typeof tenantAnnouncementWireSchema>;
export const listAnnouncementsResponseWireSchema = z.object({ announcements: z.array(tenantAnnouncementWireSchema) }).strict();

/** The status page's view of a public announcement. */
export const publicAnnouncementWireSchema = z
  .object({
    announcementId: ulidSchema,
    kind: announcementKindWireSchema,
    severity: announcementSeverityWireSchema,
    title: z.string(),
    body: z.string(),
    startsAt: isoSchema,
    endsAt: isoSchema.nullable(),
    components: z.array(statusComponentWireSchema),
    updatedAt: isoSchema,
  })
  .strict();
export type PublicAnnouncementWire = z.infer<typeof publicAnnouncementWireSchema>;
export const listPublicAnnouncementsResponseWireSchema = z.object({ announcements: z.array(publicAnnouncementWireSchema) }).strict();

/** How long an ended public announcement stays on the status page. */
export const PUBLIC_ANNOUNCEMENT_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

/** Whether an announcement is in force at `nowMs` (started, not ended). Pure; shared by the cloud and its tests. */
export function isInForce(announcement: Pick<AnnouncementWire, 'startsAt' | 'endsAt'>, nowMs: number): boolean {
  if (Date.parse(announcement.startsAt) > nowMs) return false;
  return announcement.endsAt === null || Date.parse(announcement.endsAt) > nowMs;
}

/** Whether `tenantId` is one an announcement targets. */
export function targets(announcement: Pick<AnnouncementWire, 'tenantIds'>, tenantId: string): boolean {
  return announcement.tenantIds === null || announcement.tenantIds.includes(tenantId);
}
